import { useEffect, useRef } from "react";
import SVG from "../SVG";

const defaultToneClasses = {
  info: "border-r-4 border-zi-blue-light",
  success: "border-r-4 border-zi-green-dark",
  danger: "border-r-4 border-zi-red-dark",
  warning: "border-r-4 border-yellow-500",
};

const defaultToneColors = {
  info: "#2878A3",
  success: "#15A946",
  danger: "#D71F1F",
  warning: "#EAB308",
};

const emptyList = [];

const joinClassNames = (...classes) => classes.filter(Boolean).join(" ");

const resolveNotificationId = (notification, index) =>
  notification?.id ?? notification?.key ?? `toast-${index}`;

const resolveDuration = (notification, defaultDuration) => {
  if (notification?.persistent) return null;

  const durationMs = Number(notification?.duration ?? defaultDuration);
  if (!Number.isFinite(durationMs) || durationMs <= 0) return null;

  return durationMs;
};

export default function Toast({
  notifications,
  onDismiss,
  defaultDuration = 5000,
  positionClassName = "top-16 left-0 right-0",
  containerClassName = "",
  toastClassName = "",
  titleClassName = "",
  messageClassName = "",
  closeButtonClassName = "",
  actionButtonClassName = "",
  closeLabel = "Close",
  showCloseButton = true,
  showProgress = true,
  toneClasses = defaultToneClasses,
  toneColors = defaultToneColors,
}) {
  const timersRef = useRef(new Map());
  const list = Array.isArray(notifications) ? notifications : emptyList;

  useEffect(() => {
    if (!onDismiss) return;

    const activeIds = new Set();

    list.forEach((notification, index) => {
      const notificationId = resolveNotificationId(notification, index);
      const durationMs = resolveDuration(notification, defaultDuration);
      const existingTimer = timersRef.current.get(notificationId);

      activeIds.add(notificationId);

      if (!durationMs) {
        if (existingTimer) {
          clearTimeout(existingTimer.timeoutId);
          timersRef.current.delete(notificationId);
        }
        return;
      }

      if (existingTimer && existingTimer.durationMs === durationMs) {
        return;
      }

      if (existingTimer) {
        clearTimeout(existingTimer.timeoutId);
      }

      const timeoutId = setTimeout(() => {
        timersRef.current.delete(notificationId);
        onDismiss(notificationId, notification);
      }, durationMs);

      timersRef.current.set(notificationId, { timeoutId, durationMs });
    });

    timersRef.current.forEach(({ timeoutId }, notificationId) => {
      if (!activeIds.has(notificationId)) {
        clearTimeout(timeoutId);
        timersRef.current.delete(notificationId);
      }
    });
  }, [defaultDuration, list, onDismiss]);

  useEffect(() => {
    const timers = timersRef.current;

    return () => {
      timers.forEach(({ timeoutId }) => clearTimeout(timeoutId));
      timers.clear();
    };
  }, []);

  if (!list.length) return null;

  return (
    <>
      <style>{`
        @keyframes ziToastTimeline {
          from { width: 0%; }
          to { width: 100%; }
        }
      `}</style>
      <div
        className={joinClassNames(
          "fixed z-[999] flex flex-col items-center gap-2 pointer-events-none px-2.5",
          positionClassName,
          containerClassName,
        )}
      >
        {list.map((notification, index) => {
          const notificationId = resolveNotificationId(notification, index);
          const tone = notification?.tone || "info";
          const durationMs = resolveDuration(notification, defaultDuration);
          const canDismiss =
            Boolean(onDismiss) &&
            showCloseButton &&
            notification?.dismissible !== false;
          const canShowProgress =
            Boolean(durationMs) &&
            showProgress &&
            notification?.showProgress !== false;
          const action = notification?.action;
          const actionLabel = action?.label ?? notification?.actionLabel;
          const handleAction = action?.onClick ?? notification?.onAction;

          const dismissNotification = () => {
            const existingTimer = timersRef.current.get(notificationId);

            if (existingTimer) {
              clearTimeout(existingTimer.timeoutId);
              timersRef.current.delete(notificationId);
            }

            onDismiss(notificationId, notification);
          };

          return (
            <div
              key={notificationId}
              className={joinClassNames(
                "w-full max-w-md rounded-lg px-2 py-1.5 text-xs md:text-sm bg-zi-bg1-light/80 dark:bg-zi-inputbg-dark text-zi-text-light dark:text-zi-text-dark ring-1 ring-inset ring-zi-border-dark/40 dark:ring-zi-border-light/30 relative pointer-events-auto overflow-hidden",
                // toneClasses[tone] || toneClasses.info,
                toastClassName,
                notification?.className,
              )}
            >
              <div className="grid grid-cols-[1fr,auto] items-center gap-2 min-w-0">
                <div className="min-w-0 px-2">
                  {notification?.content ? (
                    notification.content
                  ) : (
                    <>
                      {notification?.title ? (
                        <p
                          className={joinClassNames(
                            "text-sm font-medium leading-6 break-words",
                            titleClassName,
                          )}
                        >
                          {notification.title}
                        </p>
                      ) : null}
                      {notification?.message ? (
                        <p
                          className={joinClassNames(
                            "text-start leading-6 break-words",
                            messageClassName,
                          )}
                        >
                          {notification.message}
                        </p>
                      ) : null}
                    </>
                  )}
                </div>

                {(actionLabel || canDismiss) && (
                  <div className="flex items-center gap-1">
                    {actionLabel && typeof handleAction === "function" ? (
                      <button
                        type="button"
                        className={joinClassNames(
                          "px-2 text-xs font-medium text-zi-blue-light hocus:underline whitespace-nowrap",
                          actionButtonClassName,
                          action?.className,
                        )}
                        onClick={() => handleAction(notification)}
                      >
                        {actionLabel}
                      </button>
                    ) : null}

                    {canDismiss ? (
                      <button
                        type="button"
                        className={joinClassNames(
                          "text-xs text-zi-blue-light hocus:underline px-0",
                          closeButtonClassName,
                        )}
                        onClick={dismissNotification}
                        aria-label={closeLabel}
                      >
                        <SVG
                          name="closeX"
                          classname="stroke-zi-text-light dark:stroke-zi-text-dark size-8"
                        />
                      </button>
                    ) : null}
                  </div>
                )}
              </div>

              {canShowProgress ? (
                <div
                  className="absolute bottom-0 right-0 h-0.5 rounded-full"
                  style={{
                    backgroundColor: toneColors[tone] || toneColors.info,
                    animation: "ziToastTimeline linear forwards",
                    animationDuration: `${durationMs}ms`,
                    width: "100%",
                  }}
                />
              ) : null}
            </div>
          );
        })}
      </div>
    </>
  );
}
